import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import axios from 'axios';
import Toast from '@components/toast';
import { getSlug } from '@lib/utils';
import { gql } from '@apollo/client';
import { graphql } from '@lib/api';

// ---------------------------------------------------------

import AdvancedSearch from '@components/advanced-search';
import Button from '@components/button';
import Image from '@components/image';
import Card from '@components/card';
import SkeletonCard from '@components/card/skeleton';
import Container from '@layout/container';
import Grid from '@layout/grid';
import Layout from '@layout/index';
import Pagination from '@components/pagination';
import ResultsPageFilter from '@components/results-page-filter';
import SortButton from '@components/sort-button';

// ---------------------------------------------------------

import { fixtures } from '@content/results';
import { getResultsPageMeta, pushStateToQuery } from '@lib/query-utils';
import { getSearchResults } from '@lib/search-results';
import { AGENT_BY_ID_QUERY } from '@lib/fragments';
import { useAgentData } from '@lib/hooks';
import { fetchAssociatedAgent, getAssociatedAgentItem } from '@lib/associatedAgent';

import { initializeFiltersFromQuery } from '@src/store/slices/searchFiltersSlice';

// ---------------------------------------------------------

import {
  results_header,
  results_info_and_sorting_container,
  copyright_section,
  zero_state
} from './styles.module.scss';

const PAGESIZE = 15;
// ---------------------------------------------------------

// Create the initial meta values in GSP, so that crawlers have access to it
// useEffect, waiting for router, does not resolve in time leading to default
// meta values
export const getServerSideProps = async (ctx) => {
  const queryVars = ctx.query;
  const metaProps = getResultsPageMeta(queryVars);
  let isValidImage = false;
  let associatedAgent = null;

  // Fetch listing data server-side
  const { data, sortItem, errorMessage } = await getSearchResults(queryVars, 'resultsPage');

  const props = {
    metaProps: metaProps,
    initialData: {
      listings: data?.listingSearch?.items ?? [],
      totalCount: data?.listingSearch?.totalCount ?? 0,
      sortBy: sortItem,
      error: errorMessage || false,
      queryParams: queryVars // Include query params for comparison
    }
  };

  // Convert each key-value pair in queryVars to query string format
  const queryString = Object.keys(queryVars)
    .map((key) => {
      const value = queryVars[key];
      // If the value is an array, create a query string part for each element
      if (Array.isArray(value)) {
        return value
          .map((val) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`)
          .join('&');
      } else {
        // If the value is not an array, create a single query string part
        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      }
    })
    .join('&'); // Join all parts with '

  // Conditionally add `dynamicOgImage` to props if `imageURL` has a value
  if (queryVars.agentid) {
    associatedAgent = await fetchAssociatedAgent({ agentId: parseInt(queryVars.agentid) });

    let variables = {
      agentId: parseInt(queryVars.agentid)
    };

    const data = await fetchData(variables, true);

    if (typeof data?.agentSearch?.items[0] !== 'undefined' && data?.agentSearch?.items[0] != null) {
      const imageURL = process.env.NEXT_PUBLIC_VERCEL_URL
        ? `https://${process.env.NEXT_PUBLIC_VERCEL_URL}/api/og/results?${queryString}`
        : `http://localhost:3000/api/og/results?${queryString}`;

      try {
        const response = await fetch(imageURL);
        isValidImage = response.ok; // true if status is 200-299

        if (isValidImage) {
          props.dynamicOgImage = imageURL;
        }
      } catch (error) {
        console.error('Failed to fetch the image URL', error);
      }
    } else {
      console.error('Invalid Agent ID');
    }
  }

  return {
    props: {
      ...props,
      associatedAgent: getAssociatedAgentItem(associatedAgent)
    }
  };
};

const fetchData = async (variables, dynamicOgImage) => {
  const data = await graphql(
    gql`
      ${AGENT_BY_ID_QUERY}
    `,
    variables
  );

  if (typeof data?.agentSearch?.items[0] !== 'undefined' && data?.agentSearch?.items[0] != null) {
    if (dynamicOgImage) {
      return data;
    } else {
      sessionStorage.setItem('agentData', JSON.stringify(data?.agentSearch?.items[0]));
    }
  }
};

// ---------------------------------------------------------

const ResultsPage = ({
  metaProps,
  dynamicOgImage,
  initialData,
  associatedAgent: associatedAgentProp
}) => {
  const dispatch = useDispatch();
  const router = useRouter();

  const stateParams = useSelector((state) => state.searchFilters);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(initialData.error);
  const [sortBy, setSortBy] = useState(initialData.sortBy);
  const [totalCount, setTotalCount] = useState(initialData.totalCount);
  const [headerValue, setHeaderValue] = useState(metaProps.header);
  const [saveString, setSaveString] = useState('');
  const [copyrightData, setCopyrightData] = useState([]);
  const [listingData, setListingData] = useState(initialData.listings);

  // Initialize Redux store with query params, but don't refetch if we have server data
  useEffect(() => {
    if (!router.isReady) return;

    const omitProperty = (obj, propName) => {
      // eslint-disable-next-line no-unused-vars
      const { [propName]: omitted, ...rest } = obj;
      return rest;
    };

    const routerParams = router.query || {};
    const paramsWithoutAgentId = omitProperty(routerParams, 'agentid');

    // Only initialize Redux store, don't fetch data if we have server data
    dispatch(initializeFiltersFromQuery(paramsWithoutAgentId));
  }, [router.isReady, router.query, dispatch]);

  // Handle data fetching, header updates, and copyright data
  useEffect(() => {
    if (!router.isReady) return;

    const omitProperty = (obj, propName) => {
      // eslint-disable-next-line no-unused-vars
      const { [propName]: omitted, ...rest } = obj;
      return rest;
    };

    const fetchResultsData = async () => {
      const routerParams = router.query || {};
      const paramsWithoutAgentId = omitProperty(routerParams, 'agentid');

      // Update header value
      const dynamicMeta = getResultsPageMeta(paramsWithoutAgentId);
      setHeaderValue(dynamicMeta.header);

      // Fetch new data
      setLoading(true);
      const { data, sortItem, convertedRouterParams, graphQLString, errorMessage } =
        await getSearchResults(paramsWithoutAgentId, 'resultsPage');

      dispatch(initializeFiltersFromQuery(convertedRouterParams));
      setSortBy(sortItem);
      setListingData(data?.listingSearch?.items ?? []);
      setTotalCount(data?.listingSearch?.totalCount);
      setSaveString(graphQLString);

      if (errorMessage) {
        setError(true);
        setLoading(false);
        return;
      }

      // Update copyright data
      const AllCopyrightData = (data?.listingSearch?.items || []).reduce(
        (acc, { mlsCopyright }) => {
          return acc.find((item) => item.description === mlsCopyright?.description)
            ? acc
            : [...acc, mlsCopyright];
        },
        []
      );
      setCopyrightData(AllCopyrightData);
      setLoading(false);
      setError(false);
    };

    fetchResultsData();
  }, [router, dispatch]);

  const handleSortClick = (el) => {
    pushStateToQuery(router, { ...stateParams, sortby: el.label }, true);
  };

  const handlePageChange = ({ page }) => {
    pushStateToQuery(router, { ...stateParams, page: (page || 0) + 1 });
  };

  const handleAddSoldsToQuery = () => {
    pushStateToQuery(router, { ...stateParams, status: ['Sold'] });
  };

  // ---------------------------------------------------------

  const handleSaveSearch = async (searchName) => {
    try {
      await axios.post('/api/user/searches', {
        graphQL: saveString,
        name: searchName || 'Untitled Search',
        shouldNotifyOnChanges: true,
        version: 1
      });

      Toast.notify({
        message: `Successfully saved search: ${searchName}`,
        type: 'success'
      });
    } catch (error) {
      Toast.notify({
        message: `There was an error saving this search.`,
        type: 'error'
      });
    }
  };

  // ---------------------------------------------------------

  const { setAssociatedAgentToSessionStorage } = useAgentData(associatedAgentProp);

  useEffect(() => {
    // set agent data to session storage if it was provided by the server
    if (associatedAgentProp) {
      setAssociatedAgentToSessionStorage(associatedAgentProp);
    }
  }, [associatedAgentProp]);

  // ---------------------------------------------------------

  const hasZeroProperties = listingData.length === 0;
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const handleOpenModal = () => {
    setModalIsOpen(true);
  };

  // ---------------------------------------------------------

  const queryIncludedSolds =
    router.query?.status === 'Sold' || (router.query?.status || []).includes('Sold');

  return (
    <Layout dynamicOgImage={dynamicOgImage} meta={metaProps} theme="gradient">
      <Container
        backgroundImage={{
          height: '216px',
          src: '/fpo/FPO_Background_header.png',
          width: '1440px'
        }}
        backgroundImageLayout="responsive"
        gridOptions={{ layout: 'single' }}
        priority
        removeBottomSpacing
        spacing="small"
        theme="has-background-image-with-content-box-shadow"
      >
        <header className={results_header}>
          <ResultsPageFilter headerValue={headerValue} handleSaveSearch={handleSaveSearch} />
          <div className={results_info_and_sorting_container}>
            <div>
              {error ? null : loading ? (
                <p>Loading...</p>
              ) : (
                <p>{hasZeroProperties ? 0 : totalCount} properties</p>
              )}
            </div>
            <div>
              <SortButton
                options={fixtures.sortButtonOptions}
                onChange={handleSortClick}
                selection={sortBy}
              />
            </div>
          </div>
        </header>

        {!error && hasZeroProperties && (
          <>
            <div className={zero_state}>
              <p>
                {router.query.address ? fixtures.zero_state_address : fixtures.zero_state}
                <Button
                  onClick={() => handleOpenModal()}
                  onKeyDown={() => handleOpenModal()}
                  type="button"
                  theme="text-only-red"
                  label={fixtures.zero_state_link}
                  url={null}
                ></Button>
                .
              </p>

              {!queryIncludedSolds && (
                <p>
                  Would you like to{' '}
                  <Button
                    onClick={handleAddSoldsToQuery}
                    onKeyDown={handleAddSoldsToQuery}
                    type="button"
                    theme="text-only-red"
                    label={'search Sold Listings'}
                  ></Button>{' '}
                  instead?
                </p>
              )}
            </div>
          </>
        )}

        {error && (
          <>
            <div className={zero_state}>
              <p>{fixtures.error_state}</p>
              <Button theme="black" label="New Search" onClick={() => setModalIsOpen(true)} />
            </div>
          </>
        )}

        {loading && !hasZeroProperties && (
          <Grid layout="third">
            {Array.from({ length: 9 }).map((_, index) => {
              return <SkeletonCard key={index} />;
            })}
          </Grid>
        )}

        {!loading && !error && (
          <Grid layout="third">
            {(listingData || []).map((listing) => {
              return (
                <Card
                  alertPills={listing.alertPills}
                  image={{
                    src: !listing.mainPhoto
                      ? 'https://place-hold.it/340x231/green/white&text=FPO&fontsize=16'
                      : `${listing.mainPhoto.midSizeImageUrl}`
                  }}
                  property={{
                    ...listing,
                    details: [
                      { br: listing.bedroomCount },
                      {
                        ba: listing.partBathroomCount
                          ? `${listing.fullBathroomCount}/${listing.partBathroomCount}`
                          : listing.fullBathroomCount
                      },
                      // TODO: remove leading 0 e.g. 0.0436 should be .0436
                      { ac: listing.acres }
                    ],
                    price: listing.priceFormatted,
                    realtor: {
                      isActiveSibcyAgent: !listing?.isIdxListing,
                      primaryRealtorName: listing.primaryAgentDisplayOne,
                      secondaryRealtorName: listing.secondaryAgentDisplayOne
                    }
                  }}
                  url={getSlug(listing.canonicalUrl)}
                  key={`card-${listing.listingId}`}
                />
              );
            })}
          </Grid>
        )}
      </Container>

      <Container spacing="medium" textAlignment="center" gridOptions={{ layout: 'single' }}>
        {totalCount > PAGESIZE && (
          <Pagination
            items={Array.from(Array(totalCount || 0))}
            onChange={handlePageChange}
            per={PAGESIZE}
          />
        )}
      </Container>
      <Container spacing="medium" textAlignment="center" gridOptions={{ layout: 'single' }}>
        {copyrightData &&
          copyrightData.map((el, i) => {
            return (
              <section className={copyright_section} key={i}>
                <div>
                  <Image
                    layout="fill"
                    objectFit="contain"
                    image={{
                      height: '40px',
                      src: el.logoUrl,
                      width: '96px'
                    }}
                  />
                </div>
                <div>{el.description}</div>
              </section>
            );
          })}
      </Container>

      {modalIsOpen && (
        <AdvancedSearch
          modalIsOpen={modalIsOpen}
          setModalIsOpen={setModalIsOpen}
          isNewSearch={true}
        />
      )}
    </Layout>
  );
};

export default ResultsPage;
